/**
 * safetySectionUtils.js
 *
 * Shared utilities for Safety Checklist sections / partitions:
 * - normalizeSectionConfig: Normalizes section configuration contract.
 * - buildSectionedQuestionRows: Groups questions into sectioned render streams.
 * - validateSectionConfig: Client-side invariant validator.
 */

export const ALLOWED_STYLES = ["FULL_WIDTH_BAND"];
export const ALLOWED_SERIAL_MODES = ["GLOBAL", "RESET_PER_SECTION"];

export const DEFAULT_SECTION_CONFIG = {
  enabled: false,
  style: "FULL_WIDTH_BAND",
  serial_number_mode: "GLOBAL",
  sections: [],
};

/**
 * Normalizes boolean values from various formats (boolean, string, number).
 */
export const normalizeBoolean = (value, fallback = false) => {
  if (value === true || value === "true" || value === 1 || value === "1") {
    return true;
  }
  if (value === false || value === "false" || value === 0 || value === "0") {
    return false;
  }
  return fallback;
};

/**
 * Normalizes section_config with safe defaults.
 * Preserves user labels, section definitions, and serial_number_mode even when disabled.
 */
export function normalizeSectionConfig(sectionConfig) {
  const raw = sectionConfig && typeof sectionConfig === "object" ? sectionConfig : {};

  const rawSections = Array.isArray(raw.sections)
    ? raw.sections
    : [];

  const seenKeys = new Set();
  const normalizedSections = rawSections
    .filter((s) => s && typeof s === "object")
    .map((s, idx) => {
      let key = String(s.key || `section_${idx + 1}`).trim();
      if (!key) key = `section_${idx + 1}`;
      if (seenKeys.has(key)) key = `${key}_${idx + 1}`;
      seenKeys.add(key);

      const label = String(s.label ?? `Section ${idx + 1}`).trim();
      const orderIndex = Number.isFinite(Number(s.order_index)) && Number(s.order_index) > 0
        ? Number(s.order_index)
        : idx + 1;

      const rawQ = Array.isArray(s.question_order_indexes)
        ? s.question_order_indexes
        : [];
      const qIndexes = Array.from(
        new Set(
          rawQ
            .map((n) => Number(n))
            .filter((n) => Number.isFinite(n) && n > 0)
        )
      ).sort((a, b) => a - b);

      return {
        key,
        label,
        order_index: orderIndex,
        question_order_indexes: qIndexes,
      };
    })
    .sort((a, b) => a.order_index - b.order_index);

  const rawMode = String(raw.serial_number_mode || "GLOBAL")
    .trim()
    .toUpperCase();
  const finalSerialMode = ALLOWED_SERIAL_MODES.includes(rawMode)
    ? rawMode
    : "GLOBAL";

  const rawStyle = String(raw.style || "FULL_WIDTH_BAND")
    .trim()
    .toUpperCase();
  const style = ALLOWED_STYLES.includes(rawStyle)
    ? rawStyle
    : "FULL_WIDTH_BAND";

  return {
    enabled: normalizeBoolean(raw.enabled, false),
    style,
    serial_number_mode: finalSerialMode,
    sections: normalizedSections,
  };
}

/**
 * Interleaves section headers and question items for UI rendering (Preview, Maker, Checker, History, Readonly).
 *
 * @param {Object} params
 * @param {Array} params.questions - Array of question items
 * @param {Object} params.sectionConfig - section_config object
 * @param {Function} [params.getOrderIndex] - Optional function to extract 1-based order index from a question
 * @returns {Array} Stream of objects with { type: 'section', key, label } or { type: 'question', question, serial_no, global_index, section_key }
 */
export function buildSectionedQuestionRows({
  questions = [],
  sectionConfig = null,
  getOrderIndex = null,
}) {
  const norm = normalizeSectionConfig(sectionConfig);
  const items = Array.isArray(questions) ? questions : [];

  if (!norm.enabled || norm.sections.length === 0 || items.length === 0) {
    return items.map((q, idx) => ({
      type: "question",
      serial_no: idx + 1,
      global_index: idx + 1,
      section_key: null,
      question: q,
    }));
  }

  // Lookup map: 1-based question order index -> section object
  const qToSection = new Map();
  norm.sections.forEach((sec) => {
    (sec.question_order_indexes || []).forEach((qIdx) => {
      qToSection.set(Number(qIdx), sec);
    });
  });

  const result = [];
  const emittedSectionKeys = new Set();
  let currentSectionSerial = 1;
  let globalSerial = 1;

  items.forEach((q, idx) => {
    let qOrder;
    if (typeof getOrderIndex === "function") {
      qOrder = getOrderIndex(q, idx);
    } else if (q && typeof q === "object") {
      qOrder =
        q.template_order_index ??
        q.item_order ??
        q.order_index ??
        idx + 1;
    } else {
      qOrder = idx + 1;
    }

    const numOrder = Number(qOrder) || idx + 1;
    const sec = qToSection.get(numOrder);

    if (sec && !emittedSectionKeys.has(sec.key)) {
      result.push({
        type: "section",
        key: sec.key,
        label: sec.label,
        order_index: sec.order_index,
      });
      emittedSectionKeys.add(sec.key);
      currentSectionSerial = 1;
    }

    let serialNo;
    if (sec && norm.serial_number_mode === "RESET_PER_SECTION") {
      serialNo = currentSectionSerial;
      currentSectionSerial += 1;
    } else {
      serialNo = globalSerial;
    }

    globalSerial += 1;

    result.push({
      type: "question",
      serial_no: serialNo,
      global_index: globalSerial - 1,
      section_key: sec?.key || null,
      question: q,
    });
  });

  return result;
}

/**
 * Validates section configuration invariants.
 * Returns { valid: boolean, error: string | null }
 */
export function validateSectionConfig(sectionConfig, totalQuestions = null) {
  if (!sectionConfig || typeof sectionConfig !== "object") {
    return { valid: false, error: "section_config must be an object." };
  }

  if (!sectionConfig.enabled) {
    return { valid: true, error: null };
  }

  const sections = Array.isArray(sectionConfig.sections)
    ? sectionConfig.sections
    : [];

  const seenKeys = new Set();
  const assignedQuestions = new Set();

  for (let i = 0; i < sections.length; i += 1) {
    const s = sections[i];
    const key = String(s.key || "").trim();
    if (!key) {
      return { valid: false, error: `Section at position ${i + 1} is missing a key.` };
    }
    if (seenKeys.has(key)) {
      return { valid: false, error: `Duplicate section key '${key}'.` };
    }
    seenKeys.add(key);

    const label = String(s.label || "").trim();
    if (!label) {
      return { valid: false, error: `Section '${key}' must have a name/label.` };
    }

    const qIndexes = Array.isArray(s.question_order_indexes)
      ? s.question_order_indexes.map(Number)
      : [];

    for (let q = 0; q < qIndexes.length; q += 1) {
      const val = qIndexes[q];
      if (!Number.isFinite(val) || val <= 0) {
        return { valid: false, error: `Invalid question index '${val}' in section '${label}'.` };
      }
      if (totalQuestions !== null && val > totalQuestions) {
        return {
          valid: false,
          error: `Question index ${val} in section '${label}' exceeds total questions (${totalQuestions}).`,
        };
      }
      if (assignedQuestions.has(val)) {
        return {
          valid: false,
          error: `Question ${val} is assigned to multiple sections.`,
        };
      }
      assignedQuestions.add(val);
    }

    // Contiguity check
    const sorted = [...qIndexes].sort((a, b) => a - b);
    for (let c = 0; c < sorted.length - 1; c += 1) {
      if (sorted[c + 1] !== sorted[c] + 1) {
        return {
          valid: false,
          error: `Section '${label}' questions must be contiguous (found gap between ${sorted[c]} and ${sorted[c + 1]}).`,
        };
      }
    }
  }

  return { valid: true, error: null };
}
